import { getMqttDrive } from '@/drive/service'
import { BetterInputNumber } from '@/shared/BetterInputNumber'
import {
  defaultMqttProtocols,
  defaultMqttTimestamps
} from '@/shared/DefaultValues'
import { FileItem } from '@/shared/FileItem'
import {
  compressionItems,
  getOptions,
  mqttConnectionTypeItems,
  mqttPubModeItems,
  mqttSecureConns,
  mqttStoreTypeItems,
  mqttVersionItems,
  mqttWebSocketConns
} from '@/shared/SelectData'
import {
  TemplateTransferProtocolProps,
  TransferItem
} from '@/template/interface'
import { Guid } from '@/utils/guid'
import { getFormatMessage } from '@/utils/utils'
import {
  ApiOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  FolderFilled
} from '@ant-design/icons'
import {
  Modal,
  Tree,
  Form,
  Row,
  Col,
  Input,
  Tabs,
  Checkbox,
  Select,
  Menu,
  Dropdown,
  InputNumber,
  Space,
  Upload,
  Button
} from 'antd'
import { RcFile } from 'antd/lib/upload'
import React, { useEffect, useState } from 'react'

const { TabPane } = Tabs
const { confirm } = Modal

export const TemplateTransferProtocol: React.FC<TemplateTransferProtocolProps> =
  (props: TemplateTransferProtocolProps) => {
    const rootKey = 'app.protocol.mqtt'
    const [visible, setVisible] = useState<boolean>(false)
    const [treeData, setTreeData] = useState<any[]>()
    const [selectNode, setSelectNode] = useState<string>()
    const [selectKeys, setSelectKeys] = useState<string[]>([rootKey])
    const [form] = Form.useForm<TransferItem>()
    const [pubMode, setPubMode] = useState<string>()
    const [enableAuth, setEnableAuth] = useState<boolean>()
    const [connectionType, setConnectionType] = useState<string>()
    const [enableCaching, setEnableCaching] = useState<boolean>()
    const [enableCompression, setEnableCompression] = useState<boolean>(false)
    const [editItem, setEditItem] = useState<TransferItem>()
    const [drive, setDrive] = useState<FileItem>()
    const [clientIdPrefix, setClientIdPrefix] = useState<string>('$sn')

    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 7 }
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 15 }
      }
    }

    const caCrtUploadProps = {
      showUploadList: false,
      multiple: false,
      accept: '.crt',
      beforeUpload: async (file: RcFile) => {
        const text = await file.text()
        const item = form.getFieldsValue()
        item.server_config!.ca_crt_file = file.name
        item.server_config!.ca_crt = text
        form.setFieldsValue(item)
        return false
      }
    }

    const clientCrtUploadProps = {
      showUploadList: false,
      multiple: false,
      accept: '.crt',
      beforeUpload: async (file: RcFile) => {
        const text = await file.text()
        const item = form.getFieldsValue()
        item.server_config!.client_crt_file = file.name
        item.server_config!.client_crt = text
        form.setFieldsValue(item)
        return false
      }
    }

    const clientKeyUploadProps = {
      showUploadList: false,
      multiple: false,
      accept: '.crt',
      beforeUpload: async (file: RcFile) => {
        const text = await file.text()
        const item = form.getFieldsValue()
        item.server_config!.client_key_file = file.name
        item.server_config!.client_key = text
        form.setFieldsValue(item)
        return false
      }
    }

    const clientIdPrefixes = (
      <Select
        value={clientIdPrefix}
        onChange={(value) => {
          setClientIdPrefix(value)
          resetSuffix(value)
        }}
      >
        <Select.Option value='$sn'>$sn</Select.Option>
        <Select.Option value='$uuid'>$uuid</Select.Option>
      </Select>
    )

    const formatMessage = getFormatMessage()

    const loadingDrive = async () => {
      const file = await getMqttDrive(0, 0)
      setDrive(file)
    }

    const handleEditItem = (key: string, item: TransferItem) => {
      handleNewConnClick(key, item)
    }

    const handleCopyItem = (key: string, item: TransferItem) => {
      const newItem = { ...item }
      newItem.id = Guid.raw()
      newItem.enable = false
      // todo: deep clone, use for loop instead
      if (item.server_config) {
        newItem.server_config = { ...item.server_config }
        newItem.server_config.config_name = `${newItem.server_config.config_name} - copy`
      }
      if (item.timestamp_format) {
        newItem.timestamp_format = { ...item.timestamp_format }
      }
      if (item.items_config) {
        newItem.items_config = [...item.items_config]
      }
      if (item.function_config) {
        newItem.function_config = [...item.function_config]
      }
      if (item.drive) {
        newItem.drive = { ...item.drive }
      }

      handleNewConnClick(key, newItem)
    }

    const handleEnableItem = (enable: boolean, item: TransferItem) => {
      item.enable = enable
      if (props.onChange) {
        props.onChange(item)
      }
    }

    const handleDeleteItem = (item: TransferItem) => {
      confirm({
        title: formatMessage({ id: 'app.modal.confirm.delete' }),
        icon: <ExclamationCircleOutlined />,
        okText: formatMessage({ id: 'app.modal.ok' }),
        cancelText: formatMessage({ id: 'app.modal.cancel' }),
        onOk() {
          if (props.onDelete) {
            props.onDelete(item)
            const keys = [rootKey]
            handleSelect(keys)
          }
        }
      })
    }

    const setConnNodes = (parentKey: string, protocol) => {
      if (props.data && props.data.length > 0) {
        protocol.children = []
        props.data.forEach((conn) => {
          if (conn.server_config?.platform == protocol.key) {
            protocol.children.push({
              title: (
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item key='app.template.transfer.protocol.edit'>
                        <a onClick={() => handleEditItem(parentKey, conn)}>
                          {formatMessage({
                            id: 'app.template.transfer.protocol.edit'
                          })}
                        </a>
                      </Menu.Item>
                      <Menu.Item key='app.template.transfer.protocol.copy'>
                        <a onClick={() => handleCopyItem(parentKey, conn)}>
                          {formatMessage({
                            id: 'app.template.transfer.protocol.copy'
                          })}
                        </a>
                      </Menu.Item>
                      <Menu.Item key='app.template.transfer.protocol.delete'>
                        <a onClick={() => handleDeleteItem(conn)}>
                          {formatMessage({
                            id: 'app.template.transfer.protocol.delete'
                          })}
                        </a>
                      </Menu.Item>
                      <Menu.Item key='app.template.transfer.protocol.enable'>
                        <a onClick={() => handleEnableItem(!conn.enable, conn)}>
                          {formatMessage({
                            id: `app.template.transfer.protocol.enable.${conn.enable}`
                          })}
                        </a>
                      </Menu.Item>
                    </Menu>
                  }
                  trigger={['contextMenu']}
                >
                  <Space>
                    <ApiOutlined
                      style={conn.enable ? { color: '#1afa29' } : {}}
                    />
                    <span>{conn.server_config?.config_name}</span>
                  </Space>
                </Dropdown>
              ),
              key: conn.id,
              icon: null
            })
          }
        })
      }
    }

    const getClientId = () => {
      let index = 1
      if (props.data && props.data.length > 0) {
        while (true) {
          const clientId = `$sn_${index.toString().padStart(2, '0')}`
          const i = props.data.findIndex(
            (x) => x.server_config?.client_id === clientId
          )
          if (i < 0) break
          index++
        }
      }
      return index.toString().padStart(2, '0')
    }

    const resetSuffix = (key: string) => {
      let clientId = '$uuid'
      if (key === '$sn') {
        clientId = getClientId()
      }
      form.setFieldsValue({
        server_config: { client_id_suffix: clientId } as any
      })
    }

    const handleNewConnClick = (nodeData: string, item?: TransferItem) => {
      setSelectNode(nodeData)
      if (!item) {
        const defaultTimestamp = props.timestamps?.find((x) => x.isDefault)
        item = {
          enable: false,
          server_config: {
            config_name: '',
            platform: selectNode,
            client_id: `$sn_${getClientId()}`,
            ca_crt: '',
            client_crt: '',
            client_key: '',
            offline_caching: false,
            mqtt_version: '3.1.1',
            keepalive: 60,
            pub_mode: 'interval',
            pub_interval: 5000,
            with_authen: false,
            username: '$uid',
            password: '$password',
            connection_type: 'raw_tcp',
            store: 'local',
            cache_time: 100,
            cache_get_time: 10,
            server_url: props.mqttConfig?.host,
            server_port: props.mqttConfig?.port || 1883,
            path: props.mqttConfig?.path || '/mqtt',
            compress_info: {
              compress: 0
            }
          },
          timestamp_format: {
            time_format: defaultTimestamp?.format,
            time_zone: defaultTimestamp?.zone
          }
        }
      }

      // set clientid prefix
      if (item.server_config) {
        if (item.server_config.client_id?.includes('$sn_')) {
          setClientIdPrefix('$sn')
          item.server_config['client_id_suffix'] =
            item.server_config.client_id.replace('$sn_', '')
        } else {
          setClientIdPrefix('$uuid')
          item.server_config['client_id_suffix'] = '$uuid'
        }
      }

      setEditItem(item)
      setPubMode(item.server_config?.pub_mode)
      setEnableAuth(item.server_config?.with_authen)
      setConnectionType(item.server_config?.connection_type)
      setEnableCaching(item.server_config?.offline_caching)
      setEnableCompression(item.server_config?.compress_info?.compress === 1)
      form.setFieldsValue(item)
      setVisible(true)
    }

    const handleSelect = (keys: any[], item?: TransferItem) => {
      setSelectKeys(keys)
      keys.forEach((key: string) => {
        let level = 0
        if (key == rootKey) {
          level = 0
        } else {
          level = 2
        }
        if (props.onSelect) {
          if (level == 2) {
            if (!item) {
              item = props.data?.find((x) => x.id == key)
            }
            props.onSelect(level, item)
          } else {
            props.onSelect(level)
          }
        }
      })
    }

    const handleOk = () => {
      form
        .validateFields()
        .then((values: TransferItem) => {
          const merged = editItem || {}
          merged.server_config = Object.assign(
            merged.server_config,
            values.server_config
          )

          // 生成ClientId，需要验证其是否重复
          if (merged.server_config) {
            if (clientIdPrefix === '$uuid') {
              merged.server_config.client_id = '$uuid'
            } else {
              merged.server_config.client_id = `$sn_${merged.server_config['client_id_suffix']}`
            }
          }

          if (props.onFormValidate) {
            if (!props.onFormValidate(true, merged)) return
          }
          merged.timestamp_format = Object.assign(
            merged.timestamp_format,
            values.timestamp_format
          )
          if (merged.server_config) {
            merged.server_config.platform = selectNode
            if (!merged.server_config.compress_info) {
              merged.server_config.compress_info = {}
            }
            merged.server_config.compress_info.compress = +enableCompression
          }

          if (!merged.id) {
            merged.id = Guid.raw()
          }

          if (!merged.drive) {
            merged.drive = {
              soFileUrl: drive?.downloadUrl,
              soFileMd5: drive?.md5,
              checkUrl: drive?.checkUrl
            }
          }

          if (merged.timestamp_format) {
            const timestamp =
              props.timestamps?.find(
                (x) => x.format === merged.timestamp_format?.time_format
              ) || defaultMqttTimestamps[0] // 如果未找到，则使用默认值，为空下位机抛异常
            merged.timestamp_format.time_zone = timestamp?.zone
          }

          if (props.onFormSubmit) {
            props.onFormSubmit(merged)
          }
          setVisible(false)
          handleSelect([merged.id], merged)
          form.resetFields()
        })
        .catch((info) => {
          console.log(info)
        })
    }

    useEffect(() => {
      setTreeData([
        {
          title: 'MQTT',
          icon: <FolderFilled style={{ fontSize: 16, color: '#ffca28' }} />,
          key: rootKey,
          children: defaultMqttProtocols.map((p) => {
            const item = {
              title: (
                <span>
                  <span>{p.name}</span>
                  <FileOutlined
                    style={{ marginLeft: 5 }}
                    onClick={() => handleNewConnClick(p.key || '')}
                  />
                </span>
              ),
              icon: <FolderFilled style={{ fontSize: 16, color: '#28caff' }} />,
              key: p.key,
              selectable: false
            }
            setConnNodes(p.key || '', item)
            return item
          })
        }
      ])
    }, [props.data])

    useEffect(() => {
      loadingDrive()
    }, [])

    return (
      <div>
        {treeData && (
          <Tree
            showLine={{ showLeafIcon: false }}
            showIcon
            defaultExpandAll
            onSelect={(keys) => handleSelect(keys)}
            selectedKeys={selectKeys}
            treeData={treeData}
          />
        )}

        <Modal
          visible={visible}
          onCancel={() => setVisible(false)}
          title={
            editItem?.id
              ? formatMessage({
                  id: 'app.template.transfer.protocol.create.edit',
                  parameters: { name: editItem.server_config?.config_name }
                })
              : formatMessage({ id: 'app.template.transfer.protocol.create' })
          }
          width={670}
          onOk={handleOk}
          destroyOnClose={true}
          maskClosable={false}
          okText={formatMessage({ id: 'app.modal.ok' })}
          cancelText={formatMessage({ id: 'app.modal.cancel' })}
        >
          <Form form={form} {...formItemLayout}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.protocol.name'
                  })}
                  name={['server_config', 'config_name']}
                  rules={[
                    {
                      required: true,
                      transform: (value) => value?.trim(),
                      message: formatMessage({
                        id: 'app.template.transfer.protocol.name.message'
                      })
                    }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.protocol.serverUrl'
                  })}
                  name={['server_config', 'server_url']}
                  rules={[
                    {
                      required: true,
                      transform: (value) => value?.trim(),
                      message: formatMessage({
                        id: 'app.template.transfer.protocol.serverUrl.message'
                      })
                    }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.protocol.serverPort'
                  })}
                  name={['server_config', 'server_port']}
                  rules={[
                    {
                      required: true,
                      message: formatMessage({
                        id: 'app.template.transfer.protocol.serverPort.message'
                      })
                    }
                  ]}
                >
                  <InputNumber min={0} max={65535} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.protocol.clientId'
                  })}
                  name={['server_config', 'client_id_suffix']}
                  rules={[
                    {
                      required: true,
                      transform: (value) => value?.trim(),
                      message: formatMessage({
                        id: 'app.template.transfer.protocol.clientId.message'
                      })
                    }
                  ]}
                >
                  <Input
                    addonBefore={clientIdPrefixes}
                    disabled={clientIdPrefix === '$uuid'}
                    maxLength={50}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                {mqttWebSocketConns.includes(connectionType || '') && (
                  <Form.Item
                    label={formatMessage({
                      id: 'app.template.transfer.protocol.serverPath'
                    })}
                    name={['server_config', 'path']}
                    rules={[
                      {
                        required: true,
                        transform: (value) => value?.trim(),
                        message: formatMessage({
                          id: 'app.template.transfer.protocol.serverPath.message'
                        })
                      }
                    ]}
                  >
                    <Input />
                  </Form.Item>
                )}
              </Col>
              <Col span={12}></Col>
            </Row>

            <Tabs destroyInactiveTabPane={false}>
              <TabPane
                tab={formatMessage({
                  id: 'app.template.transfer.protocol.general'
                })}
                key='app.template.transfer.protocol.general'
                forceRender={true}
              >
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.transfer.protocol.mqttVersion'
                      })}
                      name={['server_config', 'mqtt_version']}
                      rules={[
                        {
                          required: true,
                          message: formatMessage({
                            id: 'app.template.transfer.protocol.mqttVersion.message'
                          })
                        }
                      ]}
                    >
                      <Select>{getOptions(mqttVersionItems)}</Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.transfer.protocol.keepAlive'
                      })}
                      name={['server_config', 'keepalive']}
                      rules={[
                        {
                          required: true,
                          message: formatMessage({
                            id: 'app.template.transfer.protocol.keepAlive.message'
                          })
                        }
                      ]}
                    >
                      <BetterInputNumber addonAfter='s' />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.transfer.protocol.pubMode'
                      })}
                      name={['server_config', 'pub_mode']}
                      rules={[
                        {
                          required: true,
                          message: formatMessage({
                            id: 'app.template.transfer.protocol.pubMode.message'
                          })
                        }
                      ]}
                    >
                      <Select onChange={(value: any) => setPubMode(value)}>
                        {getOptions(mqttPubModeItems)}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    {pubMode == 'interval' && (
                      <Form.Item
                        label={formatMessage({
                          id: 'app.template.transfer.protocol.pubInterval'
                        })}
                        name={['server_config', 'pub_interval']}
                        rules={[
                          {
                            required: true,
                            message: formatMessage({
                              id: 'app.template.transfer.protocol.pubInterval.message'
                            })
                          }
                        ]}
                      >
                        <BetterInputNumber addonAfter='ms' />
                      </Form.Item>
                    )}
                  </Col>
                </Row>
              </TabPane>

              <TabPane
                tab={formatMessage({
                  id: 'app.template.transfer.protocol.verification'
                })}
                key='app.template.transfer.protocol.verification'
                forceRender={true}
              >
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.transfer.protocol.withAuthen'
                      })}
                      name={['server_config', 'with_authen']}
                      valuePropName='checked'
                    >
                      <Checkbox
                        onChange={(e) => setEnableAuth(e.target.checked)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}></Col>
                </Row>
                {enableAuth && (
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        label={formatMessage({
                          id: 'app.template.transfer.protocol.username'
                        })}
                        name={['server_config', 'username']}
                        rules={[
                          {
                            required: true,
                            transform: (value) => value?.trim(),
                            message: formatMessage({
                              id: 'app.template.transfer.protocol.username.message'
                            })
                          }
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={formatMessage({
                          id: 'app.template.transfer.protocol.password'
                        })}
                        name={['server_config', 'password']}
                        rules={[
                          {
                            required: true,
                            transform: (value) => value?.trim(),
                            message: formatMessage({
                              id: 'app.template.transfer.protocol.password.message'
                            })
                          }
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </TabPane>

              <TabPane
                tab={formatMessage({
                  id: 'app.template.transfer.protocol.connection'
                })}
                key='app.template.transfer.protocol.connection'
                forceRender={true}
              >
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.transfer.protocol.connectionType'
                      })}
                      name={['server_config', 'connection_type']}
                    >
                      <Select
                        onChange={(value: any) => setConnectionType(value)}
                      >
                        {getOptions(mqttConnectionTypeItems)}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    {mqttSecureConns.includes(connectionType || '') && (
                      <Form.Item
                        label={formatMessage({
                          id: 'app.template.transfer.protocol.clientCrt'
                        })}
                        name={['server_config', 'client_crt_file']}
                      >
                        <Input
                          readOnly
                          className='ant-input-search ant-input-search-with-button'
                          addonAfter={
                            <Upload {...clientCrtUploadProps}>
                              <Button
                                type='primary'
                                className='ant-input-search-button'
                              >
                                <FileOutlined />
                              </Button>
                            </Upload>
                          }
                        />
                      </Form.Item>
                    )}
                    <Form.Item
                      style={{ display: 'none' }}
                      name={['server_config', 'client_crt']}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                {mqttSecureConns.includes(connectionType || '') && (
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        label={formatMessage({
                          id: 'app.template.transfer.protocol.caCrt'
                        })}
                        name={['server_config', 'ca_crt_file']}
                      >
                        <Input
                          readOnly
                          className='ant-input-search ant-input-search-with-button'
                          addonAfter={
                            <Upload {...caCrtUploadProps}>
                              <Button
                                type='primary'
                                className='ant-input-search-button'
                              >
                                <FileOutlined />
                              </Button>
                            </Upload>
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        style={{ display: 'none' }}
                        name={['server_config', 'ca_crt']}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={formatMessage({
                          id: 'app.template.transfer.protocol.clientKey'
                        })}
                        name={['server_config', 'client_key_file']}
                      >
                        <Input
                          readOnly
                          className='ant-input-search ant-input-search-with-button'
                          addonAfter={
                            <Upload {...clientKeyUploadProps}>
                              <Button
                                type='primary'
                                className='ant-input-search-button'
                              >
                                <FileOutlined />
                              </Button>
                            </Upload>
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        style={{ display: 'none' }}
                        name={['server_config', 'client_key']}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </TabPane>

              <TabPane
                tab={formatMessage({
                  id: 'app.template.transfer.protocol.timestamp'
                })}
                key='app.template.transfer.protocol.timestamp'
                forceRender={true}
              >
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.transfer.protocol.timestamp'
                      })}
                      name={['timestamp_format', 'time_format']}
                    >
                      <Select>
                        {props.timestamps?.map((x) => (
                          <Select.Option key={x.id} value={x.format || ''}>
                            {x.name}({x.format})
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}></Col>
                </Row>
              </TabPane>

              <TabPane
                tab={formatMessage({
                  id: 'app.template.transfer.protocol.other'
                })}
                key='app.template.transfer.protocol.other'
                forceRender={true}
              >
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.transfer.protocol.offlineCaching'
                      })}
                      name={['server_config', 'offline_caching']}
                      valuePropName='checked'
                    >
                      <Checkbox
                        onChange={(e) => setEnableCaching(e.target.checked)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.transfer.protocol.store'
                      })}
                      name={['server_config', 'store']}
                      rules={[
                        {
                          required: true,
                          message: formatMessage({
                            id: 'app.template.transfer.protocol.store.message'
                          })
                        }
                      ]}
                    >
                      <Select>{getOptions(mqttStoreTypeItems)}</Select>
                    </Form.Item>
                  </Col>
                </Row>

                {enableCaching && (
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        label={formatMessage({
                          id: 'app.template.transfer.protocol.cacheTime'
                        })}
                        name={['server_config', 'cache_time']}
                        rules={[
                          {
                            required: true,
                            message: formatMessage({
                              id: 'app.template.transfer.protocol.cacheTime.message'
                            })
                          }
                        ]}
                      >
                        <BetterInputNumber addonAfter='ms' />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={formatMessage({
                          id: 'app.template.transfer.protocol.cacheGetTime'
                        })}
                        name={['server_config', 'cache_get_time']}
                        rules={[
                          {
                            required: true,
                            message: formatMessage({
                              id: 'app.template.transfer.protocol.cacheGetTime.message'
                            })
                          }
                        ]}
                      >
                        <BetterInputNumber addonAfter='s' />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </TabPane>

              <TabPane
                tab={formatMessage({
                  id: 'app.template.transfer.protocol.compression'
                })}
                key='app.template.transfer.protocol.compression'
                forceRender={true}
              >
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.transfer.protocol.compress'
                      })}
                    >
                      <Checkbox
                        checked={enableCompression}
                        onChange={(e) => {
                          setEnableCompression(e.target.checked)
                          form.setFieldsValue({
                            server_config: { compress_info: { type: 'brotli' } }
                          })
                        }}
                      />
                    </Form.Item>
                  </Col>
                  {enableCompression && (
                    <Col span={12}>
                      <Form.Item
                        label={formatMessage({
                          id: 'app.template.transfer.protocol.compress.type'
                        })}
                        name={['server_config', 'compress_info', 'type']}
                      >
                        <Select>{getOptions(compressionItems)}</Select>
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </TabPane>
            </Tabs>
          </Form>
        </Modal>
      </div>
    )
  }
